import { Link } from "react-router-dom";
import Icon from "@/components/Icon";
import { PRODUCT_IMAGE_FALLBACK } from "@/lib/imageFallback";
import { getPricing } from "@/lib/pricing";

/**
 * Product card — used in listing and related sections.
 * Requires `data-testid="product-card-{slug}"` for tests.
 */
export default function ProductCard({ product, delayMs = 0 }) {
  const p = getPricing(product);

  return (
    <Link
      to={`/products/${product.slug}`}
      className="aman-card group block overflow-hidden h-full"
      data-testid={`product-card-${product.slug}`}
      style={{
        animation: `aman-fade-up 500ms ${delayMs}ms cubic-bezier(0.22,1,0.36,1) both`,
      }}
    >
      <div className="relative aspect-[4/3] bg-slate-100 overflow-hidden">
        <img
          src={product.image_url || PRODUCT_IMAGE_FALLBACK}
          alt={product.name_ar}
          className={`w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 ${
            p.outOfStock ? "opacity-60 grayscale-[35%]" : ""
          }`}
          loading="lazy"
          onError={(e) => {
            if (e.currentTarget.src !== PRODUCT_IMAGE_FALLBACK) {
              e.currentTarget.src = PRODUCT_IMAGE_FALLBACK;
            }
          }}
        />
        {product.is_featured && !p.outOfStock && (
          <span className="absolute top-3 start-3 text-[11px] font-bold text-[#0A1628] bg-[#00E5FF] px-2.5 py-1 rounded-full shadow">
            مميّز
          </span>
        )}
        {p.outOfStock && (
          <span
            className="absolute top-3 start-3 text-[11px] font-bold text-white bg-slate-800 px-2.5 py-1 rounded-full shadow"
            data-testid={`product-out-of-stock-${product.slug}`}
          >
            نفذت الكمية
          </span>
        )}
        {p.onSale && !p.outOfStock && (
          <span
            className="absolute top-3 end-3 text-[11px] font-bold text-white bg-rose-600 px-2.5 py-1 rounded-full shadow"
            data-testid={`product-discount-badge-${product.slug}`}
          >
            {`-${p.percent}%`}
          </span>
        )}
      </div>
      <div className="p-5">
        {product.category_title_ar && (
          <div className="text-[11px] text-slate-500 font-medium mb-1.5">
            {product.category_title_ar}
          </div>
        )}
        <h3 className="font-heading text-lg font-bold text-[#0A1628] leading-snug line-clamp-2 min-h-[3rem]">
          {product.name_ar}
        </h3>
        <p className="mt-2 text-slate-600 text-sm line-clamp-2 leading-relaxed">
          {product.short_desc_ar}
        </p>
        <div className="mt-4 flex items-end justify-between">
          <div>
            <div className="text-[11px] text-slate-500">ابتداءً من</div>
            {p.onSale ? (
              <div className="flex items-baseline gap-2" dir="ltr">
                <span className="font-heading text-xl font-bold text-rose-600">{p.saleStr}</span>
                <span className="text-slate-400 text-sm line-through">{p.priceStr}</span>
              </div>
            ) : (
              <div className="font-heading text-xl font-bold text-[#0A1628]" dir="ltr">
                {p.priceStr}
              </div>
            )}
          </div>
          <span className="text-[#00b8cc] group-hover:text-[#0A1628] text-sm font-semibold flex items-center gap-1 transition-colors">
            التفاصيل
            <Icon name="chevron-left" size={16} strokeWidth={2.5} />
          </span>
        </div>
      </div>
    </Link>
  );
}
